import { Controller } from "@hotwired/stimulus"
import { Chart, registerables } from "chart.js"

Chart.register(...registerables)

export default class extends Controller {
  static targets = ["sessionsChart", "costChart"]
  static values = { chartsDataUrl: String }

  connect() {
    this.currentPeriod = "weekly"
    this.sessionsChartInstance = null
    this.costChartInstance = null
    this.loadChartData()
  }

  switchPeriod(event) {
    const period = event.currentTarget.dataset.period
    if (period === this.currentPeriod) return

    this.currentPeriod = period
    event.currentTarget.parentElement.querySelectorAll("button").forEach((btn) => {
      btn.classList.remove("active")
    })
    event.currentTarget.classList.add("active")

    this.loadChartData()
  }

  async loadChartData() {
    try {
      const url = new URL(this.chartsDataUrlValue, window.location.origin)
      url.searchParams.set("period", this.currentPeriod)
      const response = await fetch(url.toString(), { credentials: "same-origin" })
      if (!response.ok) throw new Error(`HTTP ${response.status}`)
      const data = await response.json()
      this.renderCharts(data)
    } catch (error) {
      console.error("[VoiceAssistantCharts] Error loading chart data:", error)
      this.renderCharts({
        labels: [],
        session_counts: [],
        costs: [],
        period: this.currentPeriod,
      })
    }
  }

  renderCharts(data) {
    const labels = this._formatLabels(data.labels || [])
    this._renderSessionsChart(labels, data.session_counts || [])
    this._renderCostChart(labels, data.costs || [])
  }

  _renderSessionsChart(labels, counts) {
    if (this.sessionsChartInstance) {
      this.sessionsChartInstance.destroy()
      this.sessionsChartInstance = null
    }
    if (!this.hasSessionsChartTarget) return

    this.sessionsChartInstance = new Chart(this.sessionsChartTarget.getContext("2d"), {
      type: "bar",
      data: {
        labels,
        datasets: [
          {
            label: "Sessions",
            data: counts,
            backgroundColor: "rgba(54, 162, 235, 0.7)",
            borderColor: "rgba(54, 162, 235, 1)",
            borderWidth: 2,
          },
        ],
      },
      options: {
        responsive: true,
        maintainAspectRatio: false,
        plugins: { legend: { position: "top" } },
        scales: {
          y: { beginAtZero: true },
        },
      },
    })
  }

  _renderCostChart(labels, costs) {
    if (this.costChartInstance) {
      this.costChartInstance.destroy()
      this.costChartInstance = null
    }
    if (!this.hasCostChartTarget) return

    this.costChartInstance = new Chart(this.costChartTarget.getContext("2d"), {
      type: "bar",
      data: {
        labels,
        datasets: [
          {
            label: "Cost ($)",
            data: costs,
            backgroundColor: "rgba(255, 159, 64, 0.7)",
            borderColor: "rgba(255, 159, 64, 1)",
            borderWidth: 2,
          },
        ],
      },
      options: {
        responsive: true,
        maintainAspectRatio: false,
        plugins: { legend: { position: "top" } },
        scales: {
          x: {
            title: {
              display: true,
              text: this.currentPeriod === "monthly" ? "Month" : "Week",
            },
          },
          y: {
            beginAtZero: true,
            title: { display: true, text: "USD" },
            ticks: {
              callback: (value) => `$${Number(value).toFixed(6)}`,
            },
          },
        },
      },
    })
  }

  _formatLabels(labels) {
    return labels.map((label) => {
      if (this.currentPeriod === "monthly") {
        const [year, month] = label.split("-")
        return new Date(year, month - 1).toLocaleDateString("hu-HU", {
          month: "short",
          year: "numeric",
        })
      }
      const date = new Date(label)
      return date.toLocaleDateString("hu-HU", {
        year: "numeric",
        month: "short",
        day: "numeric",
      })
    })
  }
}
